import { POLL } from 'lib/elementCanvas'

export const TICKER_WEIGHT_MIN = 0.05
export const TICKER_WEIGHT_MAX = 20

const round4 = (n: number): number => Math.round(n * 10000) / 10000

const clampWeight = (n: number): number =>
  Math.min(TICKER_WEIGHT_MAX, Math.max(TICKER_WEIGHT_MIN, n))

export const tickerBlockFloorPx = (blockIndex: number, blockCount: number): number => {
  const { minPx } = POLL.ticker.blocks
  if (blockIndex === 0) return minPx.question
  if (blockIndex === blockCount - 1) return minPx.cta
  return minPx.option
}

export const tickerBlockFractions = (
  weights: readonly number[] | null | undefined,
  optionCount: number,
  layoutWidth: number
): number[] => {
  const blockCount = optionCount + 2
  if (weights != null && weights.length === blockCount && weights.every((x) => Number.isFinite(x) && x > 0)) {
    const sum = weights.reduce((s, x) => s + x, 0)
    return weights.map((x) => x / sum)
  }
  const lw = POLL.ticker.left.width
  const cw = POLL.ticker.cta.width
  const L = Math.max(1, layoutWidth)
  if (L >= lw + cw) {
    const seg = (L - lw - cw) / optionCount
    return [lw / L, ...Array.from({ length: optionCount }, () => seg / L), cw / L]
  }
  const D = lw + cw - L
  const left = lw - (D * lw) / (lw + cw)
  const cta = cw - (D * cw) / (lw + cw)
  return [left / L, ...Array.from({ length: optionCount }, () => 0), cta / L]
}

export const normalizeTickerWeights = (raw: readonly number[]): number[] => {
  const sum = raw.reduce((s, x) => s + x, 0)
  if (!(sum > 0)) return raw.map(() => 1)
  const mean = sum / raw.length
  return raw.map((x) => clampWeight(round4(x / mean)))
}

export const adoptTickerWeights = (
  weights: readonly number[] | null | undefined,
  optionCount: number,
  layoutWidth: number
): number[] => {
  if (weights != null && weights.length === optionCount + 2) return [...weights]
  return normalizeTickerWeights(tickerBlockFractions(null, optionCount, layoutWidth))
}

export const shiftTickerBoundary = (
  weights: readonly number[],
  boundary: number,
  deltaFraction: number,
  layoutWidth: number
): number[] => {
  const blockCount = weights.length
  const fractions = tickerBlockFractions(weights, blockCount - 2, layoutWidth)
  const L = Math.max(1, layoutWidth)
  const shrinking = deltaFraction > 0 ? boundary + 1 : boundary
  const floor = tickerBlockFloorPx(shrinking, blockCount) / L
  const room = Math.max(0, fractions[shrinking] - floor)
  const applied = Math.sign(deltaFraction) * Math.min(Math.abs(deltaFraction), room)
  const next = [...fractions]
  next[boundary] += applied
  next[boundary + 1] -= applied
  return normalizeTickerWeights(next)
}

export const appendTickerOptionWeight = (weights: readonly number[]): number[] => {
  const options = weights.slice(1, weights.length - 1)
  const mean = options.length > 0 ? options.reduce((s, x) => s + x, 0) / options.length : 1
  return [
    ...weights.slice(0, weights.length - 1),
    clampWeight(round4(mean)),
    weights[weights.length - 1]
  ]
}

export const removeTickerOptionWeight = (
  weights: readonly number[],
  optionIndex: number
): number[] => weights.filter((_, i) => i !== optionIndex + 1)
